import { useEffect } from 'react'
import { useAppSelector } from '../../store/hooks'

/**
 * Direct token handoff for a Godot Web export embedded on tworobots.com.
 *
 * When the site already has a logged-in session, the embedded game can skip the
 * device-code flow entirely and read the tokens directly. We expose them two
 * ways (see DEVICE_LOGIN.md → "Direct token handoff"):
 *
 *   1. Synchronous global (available at game startup):
 *        window.__TWOROBOTS_AUTH__ =
 *          { status: 'ok', accessToken, refreshToken, user } | { status: 'logged_out' }
 *
 *   2. postMessage (for iframe embeds and to react to later login/logout):
 *        window.postMessage({ type: 'tworobots:auth', status, accessToken, refreshToken, user }, origin)
 *
 *   3. Request/response: the game may ask for the current session at any time:
 *        parent.postMessage({ type: 'tworobots:auth:request' }, '*')
 *      and we reply with the same 'tworobots:auth' message.
 *
 * SECURITY: tokens are only exposed to the SAME ORIGIN (tworobots.com). We never
 * post to '*'. A cross-origin iframe cannot read window globals, so an embed on
 * another domain must use the device-code flow instead.
 */
const SessionBridge = () => {
  const { isAuthenticated, accessToken, refreshToken, user } = useAppSelector(
    state => state.auth
  )

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const session =
      isAuthenticated && accessToken
        ? { status: 'ok', accessToken, refreshToken, user }
        : { status: 'logged_out' }

    window.__TWOROBOTS_AUTH__ = session

    const publish = () => {
      try {
        window.postMessage(
          { type: 'tworobots:auth', ...session },
          window.location.origin
        )
      } catch {
        /* ignore */
      }
    }
    publish()

    // Answer explicit requests from the embedded game.
    const onMessage = e => {
      if (e.origin !== window.location.origin) return
      if (e.data && e.data.type === 'tworobots:auth:request') publish()
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [isAuthenticated, accessToken, refreshToken, user])

  return null
}

export default SessionBridge
